import { useState } from "react";
import { motion } from "framer-motion";
import { Music, Trophy, Medal, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/landing/Reveal";

const GROUPS = [
  { icon: Music, label: "Band" },
  { icon: Trophy, label: "Sports" },
  { icon: Medal, label: "ROTC" },
  { icon: Users, label: "Clubs" },
];

export const SchoolSpirit = () => {
  const [form, setForm] = useState({ name: "", phone: "", school: "", activity: "Band" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/school-claims", {
        name: form.name,
        phone: form.phone,
        school: form.school || null,
        activity: form.activity,
      });
      setDone(true);
      toast.success("School Spirit Special claimed — we'll call to arrange the free cleaning!");
    } catch {
      toast.error("Something went wrong — please call 480-940-6023.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="school-spirit" data-testid="school-spirit-section" className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-brand-blue-light to-sky-100 py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(37,99,235,0.12),transparent_50%)]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <p className="inline-block rounded-full bg-fall-orange px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.25em] text-white shadow-lg shadow-fall-orange/30">
            School Spirit Special
          </p>
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            One school uniform cleaned{" "}
            <span className="font-display text-5xl font-extrabold text-brand-blue align-middle sm:text-6xl">FREE</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
            Band, sports, ROTC, or club uniform — top &amp; bottom — cleaned completely free.
            One per student. Just claim it here or mention the School Spirit Special at drop-off.
            Free pickup &amp; delivery, like every Oxford order.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            {GROUPS.map((g, i) => (
              <motion.span
                key={g.label}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="flex items-center gap-2 rounded-full border border-brand-blue/25 bg-white px-4 py-2 text-sm font-bold text-brand-navy shadow-sm"
              >
                <g.icon className="h-4 w-4 text-fall-orange" /> {g.label}
              </motion.span>
            ))}
          </div>

          <p className="mt-6 text-sm font-semibold text-slate-500">
            Serving school families in Chandler · Maricopa · Ahwatukee · Tempe · Gilbert
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl bg-gradient-to-br from-brand-navy via-brand-navy-light to-brand-navy p-8 text-white shadow-2xl shadow-brand-blue/20">
            {done ? (
              <div data-testid="school-claim-success" className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 className="h-14 w-14 text-emerald-400" />
                <h3 className="mt-4 font-display text-xl font-bold">Claimed!</h3>
                <p className="mt-2 text-sm text-slate-300">
                  We&apos;ll call {form.phone} to arrange your free uniform cleaning. Go team!
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-xl font-bold">Claim the free uniform cleaning</h3>
                <p className="mt-2 text-sm text-slate-300">
                  Student or parent — drop your details and we&apos;ll call to arrange it.
                </p>
                <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
                  <input
                    data-testid="school-claim-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Student or parent name"
                    className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                  />
                  <input
                    data-testid="school-claim-phone-input"
                    required
                    minLength={7}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone number"
                    className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                  />
                  <input
                    data-testid="school-claim-school-input"
                    value={form.school}
                    onChange={(e) => setForm({ ...form, school: e.target.value })}
                    placeholder="School (optional)"
                    className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                  />
                  <select
                    data-testid="school-claim-activity-select"
                    value={form.activity}
                    onChange={(e) => setForm({ ...form, activity: e.target.value })}
                    className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white focus:border-fall-amber focus:outline-none [&>option]:text-brand-navy"
                  >
                    {GROUPS.map((g) => (
                      <option key={g.label}>{g.label}</option>
                    ))}
                  </select>
                  <button
                    data-testid="school-claim-submit-button"
                    type="submit"
                    disabled={sending}
                    className="rounded-xl bg-fall-amber px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-[1.02] disabled:opacity-60"
                  >
                    {sending ? "Claiming…" : "Claim my free cleaning"}
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
